import ReactDOMServer from 'react-dom/server'
import React, { useEffect, useState, createContext, useMemo } from 'react'

import merge from 'lodash/merge'
import { motion } from 'framer-motion'
import { SideNavigationProps, ISideNavigationContext } from './SideNavigation.types'
import useTheme from '../Themer/useTheme'
import Search from './Search'
import { BrowserRouter, useLocation } from 'react-router-dom'
export const SideNavigationContext = createContext<Partial<ISideNavigationContext>>({})
import './SideNavigation.scss'
import AppConfig from '../AppConfig/AppConfig'

const SideNavigation: React.FC<SideNavigationProps> = ({
    nullMessage,
    name,
    style = {},
    colorScheme,
    theme = 'default',
    isCollapsable = false,
    useDrawers = false,
    isSecondary = false,
    nestGroups = false,
    useInternalState = false,
    defaultCollapsed = false,
    expandedWidth = 150,
    initialNameKey = null,
    children,
    footer,
    header,
    onMount,
    onChange,
    search,
}) => {
    const location = useLocation()
    const themeContext = useTheme()
    const screenSizeContext = themeContext.theme.screenSizeConfig.screenSizeContext
    const getInitialCollapse = () => {
        if (!isCollapsable) {
            return false
        }
        const _defaultCollapsed = defaultCollapsed !== undefined ? defaultCollapsed : screenSizeContext !== 'xl'
        const localStorageItem = localStorage.getItem(`SideNavigation_${name}_CollapsedState`)
        return localStorageItem ? JSON.parse(localStorageItem) : _defaultCollapsed
    }

    const [initialRenderComplete, setInitialRenderComplete] = useState(false)

    const [transitionDuration, setTransitionDuration] = useState(0)
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed || getInitialCollapse())
    const [selectedCategoryId, setSelectedCategoryId] = useState(null)
    const [selectedGroupId, setSelectedGroupId] = useState(null)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [selectedNameKey, setSelectedNameKey] = useState(initialNameKey)
    const [searchResultQuery, setSearchResultQuery] = useState<any>(null)
    const [searchResultData, setSearchResultData] = useState<any>(null)
    const TRANSITION_DURATION = 300
    const COLLAPSED_WIDTH = 60

    const classNames = useMemo(() => {
        const c = ['r-side-navigation-container']

        // c.push('r-color-scheme-' + colorScheme)
        // c.push('r-theme-' + theme)

        if (isCollapsed) {
            c.push('r-state-collapsed')
        }

        if (nestGroups) {
            c.push('r-state-nested')
        }
        if (isSecondary) {
            c.push('r-side-navigation-secondary')
        }

        return c.join(' ')
    }, [isCollapsable, isCollapsed, nestGroups, isSecondary])

    const _style = {
        width: `${expandedWidth}px`,
        opacity: initialRenderComplete ? 1 : 0,
        // maxWidth: isCollapsed ? `${COLLAPSED_WIDTH}px` : `${expandedWidth}px`,
    }

    const toggleCollapse = (state) => {
        setIsTransitioning(true)
        setSelectedGroupId(null)
        setSelectedCategoryId(null)
        setIsCollapsed(state)

        localStorage.setItem(`SideNavigation_${name}_CollapsedState`, state)
        setTimeout(() => {
            setIsTransitioning(false)
        }, transitionDuration)
    }

    const renderSearchResults = () => {
        if (searchResultData) {
            return search.render(searchResultData, searchResultQuery)
        }
        return null
    }

    const variants = {
        open: { width: expandedWidth + 'px' },
        closed: { width: COLLAPSED_WIDTH + 'px' },
    }
    const onSelect = (nameKey) => {
        setSelectedNameKey(nameKey)
    }

    const context: ISideNavigationContext = {
        location,
        useDrawers,
        isCollapsed,
        isTransitioning,
        toggleCollapse,
        onChange,
        onSelect,
        selectedNameKey,
        selectedCategoryId,
        setSelectedCategoryId,
        selectedGroupId,
        setSelectedGroupId,
        nestGroups,
    }

    //on Mount
    useEffect(() => {
        setTimeout(() => setInitialRenderComplete(true), 100)
        setTimeout(() => {
            setTransitionDuration(300)
        }, 100)
        if (onMount) {
            onMount(context)
        }
    }, [])

    useEffect(() => {
        if (onChange) {
            onChange(context)
        }
    }, [context])

    // //If screensize context changes, update collapsed state
    // useEffect(() => {
    //     if (!isCollapsable) {
    //         return
    //     }
    //     if (screenSizeContext === 'xl') {
    //         setIsCollapsed(false)
    //     } else {
    //         setIsCollapsed(true)
    //     }
    // }, [screenSizeContext])

    const searchDataSource = search ? search.dataSource : null

    if (search) {
        searchDataSource.onSuccess = (res) => {
            setSearchResultData(res.data)
        }
    }

    const _theme = typeof theme === 'function' ? theme(context) : theme

    const displaySearchResults = !isCollapsed && searchResultData !== null

    const isNoChildren = ReactDOMServer.renderToStaticMarkup(<BrowserRouter>{children(context)}</BrowserRouter>) === ''
    return (
        <SideNavigationContext.Provider value={context}>
            <div
                className={`r-side-navigation-wrapper ${isCollapsed ? 'r-state-collapsed' : ''} r-theme-${
                    colorScheme || _theme
                }`}
            >
                <motion.div
                    data-testid='SideNavigation'
                    className={classNames}
                    style={merge(_style, style)}
                    animate={isCollapsed ? 'closed' : 'open'}
                    variants={variants}
                    transition={{ duration: transitionDuration / 1000 }}
                >
                    {header ? <div className={'r-side-navigation-header'}>{header(context)}</div> : null}
                    <div
                        className={'r-side-navigation-body'}
                        onScroll={() => (isCollapsed ? setSelectedGroupId(null) : null)}
                    >
                        {search ? (
                            <Search
                                renderSearchResults={renderSearchResults}
                                searchResultData={searchResultData}
                                setSearchResultData={setSearchResultData}
                                setSearchResultQuery={setSearchResultQuery}
                                search={search}
                                isCollapsed={isCollapsed}
                            />
                        ) : (
                            ''
                        )}

                        {displaySearchResults ? (
                            <div className={'r-side-navigation-Search-container'}>
                                <div className={'r-side-navigation-Search-results'}>{renderSearchResults()}</div>
                            </div>
                        ) : (
                            ''
                        )}
                        {isTransitioning ? null : displaySearchResults ? null : isNoChildren && nullMessage ? (
                            <div className={'r-sidenav-null-message'}>{nullMessage}</div>
                        ) : (
                            children(context)
                        )}
                    </div>

                    {footer ? <div className={'r-side-navigation-footer'}>{footer(context)}</div> : ''}
                </motion.div>
            </div>
        </SideNavigationContext.Provider>
    )
}

export default SideNavigation
